import { addMonths, differenceInMonths, isAfter, isBefore } from 'date-fns'

import type { DDELicenseResponse, Season } from '@img-arena/consoles-api-dde'
import Chip from '@mui/material/Chip'
import type { Column, Filter } from '@/components/table/base'
import { COLUMN_CELL_TYPE } from '@/components/table/base'
import type { FilterType } from '@/components/table/base/FilterableTable/FilterableTable.types'
import { USE_COMPETITIONS } from '@/constants/licenses'

export const LICENSE_STATUSES = {
  ACTIVE: 'active',
  FINISHED: 'finished',
  SUSPENDED: 'suspended',
  WARNING: 'warning'
} as const

type LicenseStatusType = (typeof LICENSE_STATUSES)[keyof typeof LICENSE_STATUSES]

const LICENSE_STATUS_COLORS: Record<
  LicenseStatusType,
  'error' | 'warning' | 'default' | 'primary'
> = {
  [LICENSE_STATUSES.SUSPENDED]: 'error',
  [LICENSE_STATUSES.FINISHED]: 'primary',
  [LICENSE_STATUSES.WARNING]: 'warning',
  [LICENSE_STATUSES.ACTIVE]: 'default'
}

export const LICENSE_SUBSCRIPTION_STATUSES = {
  COMPETITIONS_ALL: 'COMPETITIONS_ALL',
  COMPETITIONS_PARTIAL: 'COMPETITIONS_PARTIAL'
} as const

type LicenseSubscriptionStatusType =
  (typeof LICENSE_SUBSCRIPTION_STATUSES)[keyof typeof LICENSE_SUBSCRIPTION_STATUSES]

export type LicenseRow = {
  id: number
  year: number
  startDate: string
  endDate: string
  property: string
  numOfBookedEvents: number
  numOfConsumedEvents: number
  numOfEvents: number
  numOfSeasonOverlap: number
  status: {
    type: LicenseStatusType
    label: string
  }
  subscription: {
    competitionIds: string[]
    type: LicenseSubscriptionStatusType | null
  } | null
  unlimited: boolean
}

export function getStatus({ suspended, endDate }: { suspended: boolean; endDate: string }) {
  if (suspended) {
    return {
      type: LICENSE_STATUSES.SUSPENDED,
      label: 'Suspended'
    }
  }
  const now = new Date()
  const end = new Date(endDate)
  if (isBefore(end, now)) {
    return {
      type: LICENSE_STATUSES.FINISHED,
      label: 'Finished'
    }
  }
  const monthsLeft = differenceInMonths(addMonths(end, 1), now)
  if (monthsLeft <= 1) {
    return {
      type: LICENSE_STATUSES.ACTIVE,
      label: '< 1 mo'
    }
  } else if (monthsLeft > 11) {
    const extraMonths = monthsLeft % 12
    return {
      type: LICENSE_STATUSES.ACTIVE,
      label: `< 1 yrs${extraMonths > 0 ? ` ${extraMonths} mo` : ''}`
    }
  } else {
    return {
      type: LICENSE_STATUSES.ACTIVE,
      label: `< ${monthsLeft} mo`
    }
  }
}

export function getSeasonOverlap({
  seasons,
  endDate
}: {
  seasons: Season[]
  endDate: string
}): number {
  return seasons.reduce((acc, season) => {
    const seasonEnd = new Date(season.endDateUTC)
    const licenseEnd = new Date(endDate)

    if (isAfter(seasonEnd, licenseEnd)) {
      acc += 1
    }
    return acc
  }, 0)
}

export function getLicenseTableRows(data: DDELicenseResponse[]): LicenseRow[] {
  return data.flatMap(({ property, licensingPeriods }) => {
    if (!licensingPeriods || licensingPeriods.length === 0) return []
    const lastPeriod = licensingPeriods.reduce((latest, curr) =>
      new Date(curr.endDate) > new Date(latest.endDate) ? curr : latest
    )
    const {
      endDate,
      numOfBookedEvents,
      numOfConsumedEvents,
      numOfEvents,
      startDate,
      suspended,
      id,
      unlimited
    } = lastPeriod
    return [
      {
        id,
        year: new Date(startDate).getFullYear(),
        startDate,
        endDate,
        property,
        numOfBookedEvents,
        numOfConsumedEvents,
        numOfEvents,
        status: getStatus({ suspended, endDate }),
        subscription: lastPeriod.subscription
          ? {
              competitionIds: lastPeriod.subscription.competitionIds,
              type: lastPeriod.subscription.type
                ? (lastPeriod.subscription.type as LicenseSubscriptionStatusType)
                : null
            }
          : null,
        unlimited,
        numOfSeasonOverlap: getSeasonOverlap({
          seasons: lastPeriod.subscription ? lastPeriod.subscription.seasons || [] : [],
          endDate
        })
      }
    ]
  })
}

export const DDE_TABLE_COLUMNS: Column<LicenseRow>[] = [
  {
    type: COLUMN_CELL_TYPE.NUMBER,
    id: 'id',
    label: 'ID',
    sortAttribute: 'id'
  },
  {
    type: COLUMN_CELL_TYPE.NUMBER,
    id: 'year',
    label: 'Year',
    sortAttribute: 'year'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'startDate',
    label: 'Start Date',
    sortAttribute: 'startDate'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'endDate',
    label: 'End Date',
    sortAttribute: 'endDate'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'property',
    label: 'Property',
    sortAttribute: 'property'
  },
  {
    type: COLUMN_CELL_TYPE.NUMBER,
    id: 'numOfBookedEvents',
    label: 'Booked Events',
    sortAttribute: 'numOfBookedEvents',
    align: 'center',
    sx: { transform: 'translateX(-10px)' }
  },
  {
    type: COLUMN_CELL_TYPE.NUMBER,
    id: 'numOfConsumedEvents',
    label: 'Consumed Events',
    sortAttribute: 'numOfConsumedEvents',
    align: 'center',
    sx: { transform: 'translateX(-10px)' }
  },
  {
    type: COLUMN_CELL_TYPE.NUMBER,
    id: 'numOfEvents',
    label: 'Events',
    sortAttribute: 'numOfEvents',
    align: 'center',
    sx: { transform: 'translateX(-10px)' }
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'numOfSeasonOverlap',
    label: 'Season Overlap',
    sortAttribute: 'numOfSeasonOverlap',
    transform: (value) => (value ? 'Yes' : 'No'),
    align: 'center',
    sx: { transform: 'translateX(-10px)' }
  },
  {
    type: COLUMN_CELL_TYPE.COMPONENT,
    id: 'status',
    label: 'License status',
    render: (value: LicenseRow['status']) => {
      return (
        <Chip
          label={value.label}
          color={LICENSE_STATUS_COLORS[value.type]}
          size="small"
          variant="outlined"
        />
      )
    },
    sx: { width: 180 },
    align: 'center'
  },
  {
    type: COLUMN_CELL_TYPE.TEXT,
    id: 'subscription.type',
    label: 'Competitions Subscription',
    sortAttribute: 'subscription.type',
    transform: (value) =>
      value === USE_COMPETITIONS.ALL ? 'ALL' : value === USE_COMPETITIONS.PARTIAL ? 'PARTIAL' : '',
    align: 'center',
    sx: { transform: 'translateX(-10px)' }
  }
]

const getOptions = (data: LicenseRow[]) => {
  const propertyMap = new Map<string, FilterType>()
  const statusMap = new Map<string, FilterType>()

  data.forEach((item) => {
    const { property, status } = item
    if (property && !propertyMap.has(property)) {
      propertyMap.set(property, {
        value: property,
        label: property
      })
    }
    if (status.type && !statusMap.has(status.type)) {
      statusMap.set(status.type, {
        value: status.type,
        label: status.type.charAt(0).toUpperCase() + status.type.slice(1)
      })
    }
  })

  const sortByLabel = (arr: FilterType[]) =>
    arr.sort((a, b) => (a.label ?? '').localeCompare(b.label ?? ''))

  return {
    propertyOptions: sortByLabel(Array.from(propertyMap.values())),
    statusOptions: sortByLabel(Array.from(statusMap.values()))
  }
}

export const getDDETableFilters = (data: LicenseRow[]): Filter<LicenseRow>[] => {
  const { propertyOptions, statusOptions } = getOptions(data)

  return [
    {
      type: 'freeTextSearch',
      paramName: 'ft',
      placeholder: 'Search',
      filterOptions: (row, search) => {
        if (!search) return true
        const lowerCaseSearch = search.toLowerCase()
        return (
          row.property.toLowerCase().includes(lowerCaseSearch) ||
          row.status.label.toLowerCase().includes(lowerCaseSearch) ||
          String(row.year).toLowerCase().includes(lowerCaseSearch) ||
          row.id.toString().includes(lowerCaseSearch)
        )
      }
    },
    {
      type: 'select',
      paramName: 'property',
      label: 'Property',
      options: propertyOptions,
      width: '150px',
      filterOptions: (item, [filter]) => {
        const { property } = item
        return property === filter.value
      }
    },
    {
      type: 'select',
      paramName: 'status',
      label: 'Status',
      options: statusOptions,
      width: '150px',
      filterOptions: (item, [filter]) => {
        const { status } = item
        return status.type === filter.value
      }
    }
  ]
}

export const CSV_HEADERS = [
  'ID',
  'Period Start Date',
  'Period End Date',
  'Number of Events',
  'Number of Booked Events',
  'Number of Consumed Events',
  'Suspended',
  'Unlimited',
  'Competitions Subscription',
  'Competition IDs'
]

export const formatLicenseCsvRow = (row: LicenseRow) => [
  row.id.toString() ?? '',
  row.startDate ?? '',
  row.endDate ?? '',
  row?.numOfEvents?.toString() ?? '0',
  row?.numOfBookedEvents?.toString() ?? '0',
  row?.numOfConsumedEvents?.toString() ?? '0',
  row.status.type === LICENSE_STATUSES.SUSPENDED ? 'True' : 'False',
  row?.unlimited ? 'True' : 'False',
  row?.subscription?.type ?? '',
  row?.subscription?.competitionIds?.join(',') ?? ''
]
